import { ChevronLeft, Trash2 } from 'lucide-react';
import { DEFAULT_TEXT_STYLE } from '@tes/domain';
import { cn } from '@tes/ui';
import { PlacementSection, SignerFieldOptions, TextStyleSection } from '../../editor/fieldSections';
import { FIELD_COLORS } from '../../editor/PageCanvas';
import { INPUT_TOOLS } from '../../editor/ToolPalette';
import type { InputType } from '../../lib/types';
import { useQuickSend } from './quickSendStore';

const labelOf = (type: InputType | undefined) =>
  INPUT_TOOLS.find((t) => t.type === type)?.label ?? 'Field';

/**
 * Right-hand panel: the selected field's options (the template editor's own
 * sections, minus the role picker — every field here is the one contact's),
 * or the open document's field list when nothing is selected.
 */
export function FieldPanel() {
  const doc = useQuickSend((s) => s.docs.find((d) => d.id === s.activeId) ?? null);
  const selectedUid = useQuickSend((s) => s.selectedUid);
  const { updateField, removeField, select, setPage } = useQuickSend.getState();

  if (!doc || doc.state !== 'ready') {
    return <p className="p-4 text-sm text-slate-400">Fields you place show up here.</p>;
  }

  const field = doc.fields.find((f) => f.uid === selectedUid);
  if (!field) {
    const sorted = doc.fields.slice().sort((a, b) => a.page - b.page || a.y - b.y || a.x - b.x);
    return (
      <div className="p-4">
        <h3 className="mb-2 text-sm font-semibold text-slate-800">Fields ({doc.fields.length})</h3>
        {sorted.length === 0 ? (
          <p className="text-sm text-slate-400">
            None yet. Use the <strong>Add</strong> buttons above the page, then drag the box into place.
          </p>
        ) : (
          <ul className="space-y-1">
            {sorted.map((f) => (
              <li key={f.uid}>
                <button
                  type="button"
                  onClick={() => {
                    setPage(f.page);
                    select(f.uid);
                  }}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-50"
                >
                  <span className={cn('h-3 w-3 shrink-0 rounded-sm border', f.type && FIELD_COLORS[f.type])} />
                  <span className="flex-1">{labelOf(f.type)}</span>
                  <span className="text-xs text-slate-400">
                    p{f.page}
                    {f.required ? '' : ' · optional'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => select(null)}
          className="flex items-center gap-1 text-sm font-semibold text-slate-800 hover:text-brand-700"
          title="Back to all fields"
        >
          <ChevronLeft size={16} /> {labelOf(field.type)}
        </button>
        <button
          type="button"
          onClick={() => removeField(field.uid)}
          className="flex items-center gap-1 rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50"
        >
          <Trash2 size={14} /> Delete
        </button>
      </div>
      <SignerFieldOptions field={field} update={updateField} />
      <TextStyleSection field={field} style={DEFAULT_TEXT_STYLE} update={updateField} />
      <PlacementSection field={field} pageCount={doc.pageSizes.length} update={updateField} />
    </div>
  );
}
